import { Box, Button } from '@chakra-ui/react';
import React from 'react';
import { Link as RouteLink } from 'react-router-dom';

import { finleyAssert } from '@finley-technologies/common';

import {
  UpdateFundingRequestComponentChangesetGQLType,
  FundingRequestComponentTypeEnumGQLType,
  FundingRequestWithComponentsAndBankAccountsFragmentGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';
import {
  BankAccountMap,
  FundingRequestComponentContainer,
} from 'types/fundingRequests';

import { FundingRequestComponentsTable } from '~/borrower/domains/fundingRequests/ui/shared/ComponentsTable';
import { LoadingView } from '~/borrower/domains/fundingRequests/ui/shared/Loading';
import { Icon } from '~/packages/common-frontend/components/icons';
import { PageHeader } from '~/packages/common-frontend/components/PageContainer';
import { useGetFundingRequestWithFullDetailsAndConstraintQuery } from '~/packages/funding-requests/requests/requests.gql.generated';

// Constants
const LANDING_PAGE_STATUS_LABELS = {
  pending: {
    headerLabel: 'Review and submit draft funding request',
    subheaderLabel: 'Funding Request is awaiting your review and submission.',
  },
  submitted: {
    headerLabel: 'View submitted funding request',
    subheaderLabel: 'Funding Request is awaiting capital provider approval.',
  },
  approved: {
    headerLabel: 'View approved funding request',
    subheaderLabel: 'Your request has been approved.',
  },
} as const;

// Helpers
const getViewLinkParams = (id: string, status: string): [string, string] => {
  const baseUrl = `/funding-requests/${id}`;
  if (status === 'pending') {
    return ['Review', baseUrl];
  }
  return ['View', `${baseUrl}#monitor`];
};

const isFundingRequestComponentsTableParams = (
  val: unknown,
): val is FundingRequestComponentContainer => {
  return (
    !!val &&
    typeof val === 'object' &&
    'curePaydown' in val &&
    'collectionsPaydown' in val &&
    'collectionsRecycle' in val &&
    'drawRequest' in val
  );
};

// Exports
export const FundingRequestLandingCurrentRequest = ({
  currentFundingRequest,
  bankAccountMap,
}: {
  currentFundingRequest: FundingRequestWithComponentsAndBankAccountsFragmentGQLType;
  bankAccountMap: BankAccountMap;
}) => {
  const { id, status, fundingRequestComponents } = currentFundingRequest;
  // NOTE: All down-stream helper functions relies on the fact we're only passing in supported status for this page, so we will assert
  // for valid status here.

  const fundingRequestConstraintQuery = useGetFundingRequestWithFullDetailsAndConstraintQuery(
    { variables: { fundingRequestId: id }, fetchPolicy: 'network-only' },
  );
  const fundingRequestConstraint =
    fundingRequestConstraintQuery.data?.fundingRequestConstraint;

  if (fundingRequestConstraintQuery.error) {
    throw fundingRequestConstraintQuery.error;
  }
  if (fundingRequestConstraintQuery.loading || !fundingRequestConstraint) {
    return <LoadingView />;
  }
  finleyAssert(
    status === 'pending' || status === 'submitted' || status === 'approved',
    'Unsupported FundingRequest status',
  );

  const [linkText, linkUrl] = getViewLinkParams(id, status);

  // transform list of components to map
  // TODO: use map here
  const components = fundingRequestComponents.reduce((acc, component) => {
    const _acc = { ...acc };
    if (!_acc[component.type]) {
      _acc[component.type] = [];
    }
    _acc[component.type].push(component);

    return _acc;
  }, {} as { [key in FundingRequestComponentTypeEnumGQLType]: UpdateFundingRequestComponentChangesetGQLType[] });

  finleyAssert(
    isFundingRequestComponentsTableParams(components),
    'Funding request does not contain all necesary components to render componentsTable',
  );

  const statusLabel = LANDING_PAGE_STATUS_LABELS[status];
  return (
    <Box>
      {statusLabel && (
        <PageHeader
          headerLabel={statusLabel.headerLabel}
          subheaderLabel={statusLabel.subheaderLabel}
        />
      )}
      <FundingRequestComponentsTable
        fundingRequestComponents={components}
        isCollectionsRecycleEnabled={
          fundingRequestConstraint.isCollectionsRecycleEnabled
        }
        isVoluntaryPaydownAllowed={
          fundingRequestConstraint.isVoluntaryPaydownAllowed
        }
        maxFundingRequestAmount={
          fundingRequestConstraint.maxFundingRequestAmount
        }
        bankAccountMap={bankAccountMap}
      />
      <Button
        as={RouteLink}
        to={linkUrl}
        variant="secondary"
        colorScheme="gray"
        rightIcon={<Icon icon="arrow-right" />}
        width="fit-content"
      >
        {linkText}
      </Button>

      {status === 'submitted' && (
        <Box pt={20}>
          <PageHeader
            headerLabel="Initiate new funding request"
            subheaderLabel="To initiate a new funding request, please view and cancel any existing funding requests."
          />
        </Box>
      )}
    </Box>
  );
};
